import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraActionList, SpecraActivityFeed, SpecraBadge, SpecraButton, SpecraDashboardPage, SpecraDataTable, SpecraEmptyState, SpecraEntityCell, SpecraInlineBanner, SpecraLink, SpecraPageHeader, SpecraSectionHeader, SpecraSegmentedControl, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { differenceInCalendarDays, format } from 'date-fns';
import { useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecordBreadcrumbs } from '../../components/RecordShell/useRecordBreadcrumbs';
import { renderRouterLink } from '../../lib/renderRouterLink';
import { useLayoutStore } from '../../lib/stores/LayoutStore';
import { useOpenRecompetePursuit } from '../Capture/record/recompeteHooks';
import { growthCrumbs } from '../GrowthHub/growthCrumbs';
import { useGrowthThreadEnabled } from '../GrowthHub/growthFlags';
import { useGrowthHomeLens } from './growthHomeLens';
import { useGrowthHomeWork, useMarkGrowthHomeSeen } from './useGrowthHomeWork';
/** KPI help text (FE-owned copy; GRW-21 lists the four strings). */ export const KPI_INFO = {
    live_pursuits: 'Active pursuits you own or work on (team: all you can see)',
    rfi_due: 'Your open RFI responses due in the next 14 days',
    decisions_due: 'Go/no-go gates scheduled in the next 14 days (reviews marked as a gate)',
    proposals_in_flight: 'Proposals not yet submitted'
};
const STATUS_TONES = [
    'neutral',
    'success',
    'warning',
    'danger',
    'info'
];
function toTone(t) {
    return STATUS_TONES.includes(t) ? t : undefined;
}
function severityVariant(severity) {
    if (severity === 'danger') return 'danger';
    if (severity === 'warning') return 'warning';
    return 'neutral';
}
/** "Monday" when within the last 7 days, else "3 Nov". */ export function sinceLabel(changesSince, now = new Date()) {
    const d = new Date(changesSince);
    if (Number.isNaN(d.getTime())) return 'your last visit';
    return differenceInCalendarDays(now, d) < 7 ? format(d, 'EEEE') : format(d, 'd MMM');
}
function OpenRecompeteAction({ item }) {
    const navigate = useNavigate();
    const open = useOpenRecompetePursuit();
    // One request id per item for this mount: a retry never opens two pursuits.
    const requestId = useMemo(()=>crypto.randomUUID(), []);
    return /*#__PURE__*/ _jsx(SpecraButton, {
        variant: "link",
        size: "sm",
        disabled: open.isPending,
        onClick: ()=>open.mutate({
                programId: item.programId,
                requestId
            }, {
                onSuccess: (resp)=>navigate(`/capture/${resp.captureProjectId}`)
            }),
        children: "Open a pursuit →"
    });
}
const PURSUIT_COLUMNS = [
    {
        id: 'pursuit',
        header: 'Pursuit',
        cell: (row)=>/*#__PURE__*/ _jsx(SpecraEntityCell, {
                title: /*#__PURE__*/ _jsx(SpecraLink, {
                    href: row.href,
                    renderLink: renderRouterLink,
                    children: row.name
                }),
                description: row.subtitle || undefined
            })
    },
    {
        id: 'customer',
        header: 'Customer',
        cell: (row)=>row.customer?.href ? /*#__PURE__*/ _jsx(SpecraLink, {
                href: row.customer.href,
                renderLink: renderRouterLink,
                children: row.customer.label
            }) : row.customer?.label || '—'
    },
    {
        id: 'where',
        header: 'Where it is',
        cell: (row)=>/*#__PURE__*/ _jsx("div", {
                className: "flex flex-wrap gap-1",
                children: row.labels.map((l)=>/*#__PURE__*/ _jsx(SpecraBadge, {
                        variant: l.tone || 'neutral',
                        children: l.label
                    }, l.key))
            })
    },
    {
        id: 'next',
        header: 'Next',
        cell: (row)=>row.nextLabel || '—'
    },
    {
        id: 'last',
        header: 'Last activity',
        cell: (row)=>/*#__PURE__*/ _jsx(SpecraText, {
                size: "meta",
                tone: row.quietDays >= 45 ? 'danger' : 'muted',
                children: row.lastActivityLabel || '—'
            })
    }
];
/**
 * GRW-22: Growth Home v2 (mockup GrowthHome.png) over GRW-21's
 * `GetGrowthHomeWork` v2 fields. Behind growth_record_shell_v1 (the switch is
 * in GrowthHomePage); the "Changed since" block also needs growth_thread_v1.
 * No economics cards.
 */ export function GrowthHomeV2() {
    const navigate = useNavigate();
    const thread = useGrowthThreadEnabled();
    const [lens, select] = useGrowthHomeLens();
    const work = useGrowthHomeWork(lens);
    const markSeen = useMarkGrowthHomeSeen();
    const setShowHeader = useLayoutStore((s)=>s.setShowHeader);
    useEffect(()=>{
        setShowHeader(false);
        return ()=>setShowHeader(true);
    }, [
        setShowHeader
    ]);
    useRecordBreadcrumbs(growthCrumbs({
        label: 'Growth Home'
    }));
    // Record the visit once per mount, after the first successful load.
    const marked = useRef(false);
    const { mutate: mark } = markSeen;
    useEffect(()=>{
        if (!thread || !work.isSuccess || marked.current) return;
        marked.current = true;
        mark();
    }, [
        thread,
        work.isSuccess,
        mark
    ]);
    const data = work.data;
    const attention = data?.attention ?? [];
    const attentionItems = attention.map((a)=>({
            id: a.id,
            leading: a.badgeLabel ? /*#__PURE__*/ _jsx(SpecraBadge, {
                variant: severityVariant(a.severity),
                children: a.badgeLabel
            }) : undefined,
            label: a.title,
            description: a.explanation,
            actions: a.kind === 'recompete_unpursued' ? /*#__PURE__*/ _jsx(OpenRecompeteAction, {
                item: a
            }) : a.href ? /*#__PURE__*/ _jsxs(SpecraLink, {
                href: a.href,
                renderLink: renderRouterLink,
                children: [
                    a.actionLabel || 'Open',
                    " →"
                ]
            }) : undefined
        }));
    const feed = useMemo(()=>{
        const rows = [
            ...(data?.threadChanges ?? []).map((c)=>({
                    id: c.id,
                    who: c.pursuitName,
                    text: c.text,
                    at: c.occurredAt
                })),
            ...(data?.changes ?? []).map((c)=>({
                    id: c.id,
                    who: c.contexts[0]?.title ?? '',
                    text: c.title,
                    at: c.recordedAt
                }))
        ];
        return rows.sort((a, b)=>b.at.localeCompare(a.at)).slice(0, 10).map((r)=>({
                id: r.id,
                title: /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        r.who ? /*#__PURE__*/ _jsx(SpecraText, {
                            as: "span",
                            className: "font-medium",
                            children: r.who
                        }) : null,
                        r.who ? ' · ' : '',
                        r.text
                    ]
                }),
                timestamp: r.at ? format(new Date(r.at), 'd MMM') : undefined
            }));
    }, [
        data?.threadChanges,
        data?.changes
    ]);
    return /*#__PURE__*/ _jsx("div", {
        "data-testid": Testids.growthHome.page,
        children: /*#__PURE__*/ _jsx(SpecraDashboardPage, {
            padding: "page",
            header: /*#__PURE__*/ _jsx(SpecraPageHeader, {
                titleSize: "ui",
                title: "Growth Home",
                description: "Your pursuits and the workspaces under them",
                actions: /*#__PURE__*/ _jsx(SpecraSegmentedControl, {
                    label: "Scope",
                    value: lens,
                    onValueChange: select,
                    options: [
                        {
                            value: 'my',
                            label: 'My work',
                            testId: Testids.growthHome.my
                        },
                        {
                            value: 'team',
                            label: 'Team work',
                            testId: Testids.growthHome.team
                        }
                    ]
                })
            }),
            metrics: {
                metrics: (data?.kpis ?? []).map((k)=>({
                        id: k.key,
                        label: k.label,
                        value: String(k.value),
                        note: k.note,
                        tone: toTone(k.tone),
                        href: k.href || undefined,
                        info: KPI_INFO[k.key]
                    })),
                renderLink: renderRouterLink,
                loading: work.isLoading,
                getItemTestId: (m)=>`${Testids.growthHome.kpi}-${m.id}`
            },
            children: /*#__PURE__*/ _jsxs("div", {
                className: "space-y-8",
                children: [
                    work.isLoading && /*#__PURE__*/ _jsx("output", {
                        "data-testid": Testids.growthHome.loading,
                        children: "Loading Growth Home…"
                    }),
                    work.isError && /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                        tone: "danger",
                        variant: "framed",
                        action: /*#__PURE__*/ _jsx(SpecraButton, {
                            variant: "secondary",
                            size: "sm",
                            onClick: ()=>work.refetch(),
                            children: "Retry"
                        }),
                        children: "Growth Home could not be loaded."
                    }),
                    /*#__PURE__*/ _jsxs("section", {
                        className: "space-y-3",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                                variant: "group",
                                title: `Needs you (${attention.length})`
                            }),
                            attention.length === 0 && work.isSuccess ? /*#__PURE__*/ _jsx(SpecraEmptyState, {
                                size: "compact",
                                "data-testid": Testids.growthHome.empty,
                                title: "Nothing needs you right now."
                            }) : /*#__PURE__*/ _jsx(SpecraActionList, {
                                "data-testid": Testids.growthHome.attention,
                                items: attentionItems,
                                renderLink: renderRouterLink,
                                getItemTestId: ()=>Testids.growthHome.attentionItem
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("section", {
                        className: "space-y-3",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                                variant: "group",
                                title: lens === 'my' ? 'My pursuits' : 'Team pursuits'
                            }),
                            /*#__PURE__*/ _jsx(SpecraDataTable, {
                                columns: PURSUIT_COLUMNS,
                                rows: data?.pursuitRows ?? [],
                                loading: work.isLoading,
                                getRowId: (r)=>r.captureProjectId,
                                testId: Testids.growthHome.pursuits,
                                rowTestId: Testids.growthHome.workItem,
                                onRowClick: (r)=>navigate(r.href),
                                getRowActionLabel: (r)=>`Open ${r.name}`,
                                emptyTitle: "No live pursuits"
                            })
                        ]
                    }),
                    thread && /*#__PURE__*/ _jsxs("section", {
                        className: "space-y-3",
                        "data-testid": Testids.growthHome.changes,
                        children: [
                            /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                                variant: "group",
                                title: `Changed on your pursuits since ${sinceLabel(data?.changesSince ?? '')}`
                            }),
                            data?.changesUnavailable && /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                                tone: "warning",
                                variant: "framed",
                                children: "Market changes could not be loaded."
                            }),
                            /*#__PURE__*/ _jsx(SpecraActivityFeed, {
                                items: feed,
                                emptyText: "Nothing changed since your last visit."
                            })
                        ]
                    })
                ]
            })
        })
    });
}
